// Catálogo en memoria para el POS: productos, lotes y promociones cargados una vez (al iniciar sesión).
// Escanear/agregar productos NO consulta al servidor; solo se consulta al iniciar sesión y al registrar la venta.
import { api } from "./api";
import { cacheSet, cacheGet } from "./offline";

const state = { products: [], batches: {}, promos: [], customers: [], loadedAt: 0, loading: null };
const byBarcode = new Map(); // barcode -> { product, variant }
const byId = new Map();
const listeners = new Set();

function index() {
  byBarcode.clear(); byId.clear();
  for (const p of state.products) {
    byId.set(p.id, p);
    if (p.barcode) byBarcode.set(String(p.barcode), { product: p, variant: "" });
    for (const c of p.barcodes || []) if (c) byBarcode.set(String(c), { product: p, variant: "" });
    for (const v of p.variants || []) if (v.barcode) byBarcode.set(String(v.barcode), { product: p, variant: v.name || "" });
  }
}
function emit() { listeners.forEach((cb) => { try { cb(snapshot()); } catch { /* noop */ } }); }
export function onCatalog(cb) { listeners.add(cb); return () => listeners.delete(cb); }
export const snapshot = () => ({ products: state.products, batches: state.batches, promos: state.promos, customers: state.customers, loadedAt: state.loadedAt });
export const catalogLoaded = () => state.loadedAt > 0;
export const catalogAge = () => (state.loadedAt ? Date.now() - state.loadedAt : Infinity);

// Carga completa (una sola ronda de peticiones). force=false reutiliza si tiene menos de maxAgeMs.
export async function loadCatalog({ force = false, maxAgeMs = 15 * 60 * 1000 } = {}) {
  if (!force && state.loadedAt && catalogAge() < maxAgeMs) return snapshot();
  if (state.loading) return state.loading;
  state.loading = (async () => {
    try {
      const [p, b, pr, c] = await Promise.all([
        api.get("/products"), api.get("/products/batches/all").catch(() => ({ data: {} })),
        api.get("/promotions?only_active=true").catch(() => ({ data: [] })), api.get("/customers").catch(() => ({ data: [] })),
      ]);
      state.products = Array.isArray(p.data) ? p.data : [];
      state.batches = b.data || {};
      state.promos = pr.data || [];
      state.customers = c.data || [];
      state.loadedAt = Date.now();
      index(); cacheSet("catalog/batches", state.batches); emit();
      return snapshot();
    } finally { state.loading = null; }
  })();
  return state.loading;
}

// Recarga solo las promociones (ligero) y avisa a los escuchas; se usa al entrar al POS y al editar promociones
export async function refreshPromos() {
  try {
    const { data } = await api.get("/promotions?only_active=true");
    state.promos = Array.isArray(data) ? data : [];
    if (state.loadedAt) emit();
    return state.promos;
  } catch { return state.promos; }
}

// Rehidratar desde caché offline (para arrancar sin red)
export function hydrateFromCache() {
  if (state.loadedAt) return;
  const p = cacheGet("products");
  if (Array.isArray(p) && p.length) {
    state.products = p; state.batches = cacheGet("catalog/batches") || {}; state.promos = cacheGet("promotions?only_active=true") || [];
    state.customers = cacheGet("customers") || []; state.loadedAt = Date.now() - 14 * 60 * 1000; index();
  }
}

export const findByBarcode = (code) => byBarcode.get(String(code).trim()) || null;
export const findById = (id) => byId.get(id) || null;
export function search(term, limit = 60) {
  const t = (term || "").toLowerCase().trim();
  if (!t) return state.products.slice(0, limit);
  return state.products.filter((p) => (p.name || "").toLowerCase().includes(t) || (p.barcode || "").includes(t) || (p.barcodes || []).some((c) => c.includes(t)) || (p.sku || "").toLowerCase().includes(t)
    || (p.variants || []).some((v) => (v.barcode || "").includes(t))).slice(0, limit);
}
export const batchesFor = (pid) => (state.batches[pid] || []).filter((b) => Number(b.quantity) > 0);

// Ajustes locales tras vender (sin volver a pedir el catálogo)
export function applySaleLocally(items = []) {
  for (const it of items) {
    const p = byId.get(it.product_id);
    if (!p || p.non_inventoriable) continue;
    const q = Number(it.qty || 0);
    if (it.variant && p.has_variants) { const v = (p.variants || []).find((x) => x.name === it.variant); if (v) v.stock = +((v.stock || 0) - q).toFixed(3); }
    p.stock = +((p.stock || 0) - q).toFixed(3);
    if (it.batch_id && state.batches[p.id]) { const b = state.batches[p.id].find((x) => x.id === it.batch_id); if (b) b.quantity = +((b.quantity || 0) - q).toFixed(3); }
  }
  cacheSet("products", state.products); cacheSet("catalog/batches", state.batches); emit();
}
export function invalidateCatalog() { state.loadedAt = 0; }

// Vigila la versión del catálogo en el servidor (petición mínima) y recarga cuando cambia
let _version = null;
export async function checkCatalogVersion() {
  try {
    const { data } = await api.get("/catalog/version");
    if (_version !== null && data.version !== _version) { _version = data.version; await loadCatalog({ force: true }); return true; }
    _version = data.version;
  } catch { /* sin red */ }
  return false;
}
export function watchCatalog(intervalMs = 20000) {
  checkCatalogVersion();
  const t = setInterval(() => { if (document.visibilityState === "visible") checkCatalogVersion(); }, intervalMs);
  const onVis = () => { if (document.visibilityState === "visible") checkCatalogVersion(); };
  document.addEventListener("visibilitychange", onVis);
  window.addEventListener("focus", onVis);
  return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
}
